import React from "react";
import { motion } from "framer-motion";
import { MapPin, Users, DollarSign, ShieldCheck, ArrowRight } from "lucide-react";

/**
 * TopCompanies Component - Compact Twin-Column Power List
 * Reduces scrolling by 50% while maintaining high-impact horizontal infographics.
 */
const TopCompanies = ({ data }) => {
    if (!data || data.length === 0) return null;

    return (
        <div className="mt-12 mb-20 max-w-[1400px] mx-auto px-4">
            {/* Header Section - More Compact */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-10">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-600 rounded-2xl text-white shadow-lg shadow-blue-100">
                        <ShieldCheck size={24} />
                    </div>
                    <div>
                        <h2 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tighter leading-none mb-1">
                            Utah's <span className="text-blue-600">Construction</span> Titans
                        </h2>
                        <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Top 10 Industry Powerhouses</p>
                    </div>
                </div>
                <div className="hidden lg:block text-right">
                    <p className="text-[10px] font-black text-gray-300 uppercase tracking-[0.3em]">Corporate Impact Index • Q1 2026</p>
                </div>
            </div>

            {/* Twin-Column Power Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                {data.map((company, index) => (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                            delay: (index % 5) * 0.05,
                            type: "spring",
                            stiffness: 100,
                            damping: 15
                        }}
                        whileHover={{
                            x: 8,
                            backgroundColor: "rgba(248, 250, 252, 1)",
                            borderColor: "rgba(59, 130, 246, 0.2)"
                        }}
                        className="group relative bg-white border border-gray-100 rounded-[1.5rem] p-4 transition-all duration-300 flex items-center gap-4 hover:shadow-xl hover:shadow-blue-50/20 active:scale-[0.98]"
                    >
                        {/* Compact Rank */}
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center border border-gray-100 group-hover:bg-blue-600 group-hover:border-blue-600 transition-colors duration-300">
                            <span className="text-sm font-black text-gray-400 group-hover:text-white transition-colors">
                                {String(index + 1).padStart(2, '0')}
                            </span>
                        </div>

                        {/* Identity & Core Info */}
                        <div className="flex-grow min-w-0">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-1">
                                <h3 className="text-sm md:text-base font-black text-gray-900 group-hover:text-blue-600 transition-colors leading-tight">
                                    {company.Company}
                                </h3>
                                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-gray-50 rounded-full text-[9px] font-bold text-gray-400 group-hover:bg-blue-50 group-hover:text-blue-500 transition-colors whitespace-nowrap">
                                    <MapPin size={10} />
                                    {company.Headquarters}
                                </div>
                            </div>

                            {/* Domain Info */}
                            <p className="text-[10px] text-gray-400 font-medium opacity-60 group-hover:opacity-100 transition-opacity leading-relaxed">
                                {company.Industry}
                            </p>
                        </div>

                        {/* High-Impact Metrics */}
                        <div className="flex items-center gap-4 md:gap-6 flex-shrink-0 ml-2">
                            {/* Revenue */}
                            <div className="text-right">
                                <div className="text-[8px] font-black text-emerald-500 uppercase tracking-widest mb-0.5">Revenue</div>
                                <div className="text-xs md:text-sm font-black text-gray-700 tracking-tighter tabular-nums">
                                    {company["Estimated Annual Revenue"]}
                                </div>
                            </div>

                            {/* Employees */}
                            <div className="text-right border-l border-gray-100 pl-4">
                                <div className="text-[8px] font-black text-blue-500 uppercase tracking-widest mb-0.5">Force</div>
                                <div className="text-xs md:text-sm font-black text-gray-700 tracking-tighter tabular-nums">
                                    {company["Estimated Employee Count"]}
                                </div>
                            </div>
                        </div>

                        {/* Minimal Indicator */}
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-blue-600 rounded-r-full opacity-0 group-hover:opacity-100 transition-opacity" />
                    </motion.div>
                ))}
            </div>

            {/* Compact Footer */}
            <div className="mt-8 flex items-center justify-center gap-8">
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-[9px] font-bold text-gray-300 uppercase tracking-widest">Revenue Integrity</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    <span className="text-[9px] font-bold text-gray-300 uppercase tracking-widest">Verified Workforce</span>
                </div>
            </div>
        </div>
    );
};

export default TopCompanies;
